<template>
  <q-select
    ref="selectRef"
    :class="[
      'filter-select',
      {
        'has-value': modelValue && modelValue.length > 0
      }
    ]"
    filled
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    multiple
    use-input
    new-value-mode="add-unique"
    map-options
    emit-value
    :options="displayedOptions"
    option-label="label"
    option-value="value"
    :placeholder="placeholder"
    :hint="hint"
    :rules="[validateEmailRule]"
    @new-value="createValue"
    @filter="handleFilter"
    behavior="menu"
    popup-content-class="select-content"
  >
    <template v-slot:option="scope">
      <q-item v-bind="scope.itemProps">
        <q-item-section>
          <q-item-label>{{ scope.opt.label }}</q-item-label>
          <q-item-label caption>{{ scope.opt.value }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>
    <template v-slot:selected-item="scope">
      <q-chip
        removable
        dense
        square
        @remove="scope.removeAtIndex(scope.index)"
        :tabindex="scope.tabindex"
        :color="isValidEmail(getSelectedLabel(scope.opt)) ? 'primary' : 'red'"
        text-color="white"
      >
        {{ getSelectedLabel(scope.opt) }}
      </q-chip>
    </template>
    <template v-slot:no-option>
      <q-item>
        <q-item-section class="text-grey">
          Aucun résultat
        </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>

<script>
import { computed, ref } from 'vue'
import { stringToSlug } from 'src/utils/stringToSlug'

export default {
  name: 'SwMultipleSelect',
  props: {
    modelValue: {
      type: Array,
      required: true,
      default: () => []
    },
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    errorMessage: {
      type: String,
      default: 'Adresse e-mail invalide'
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const selectRef = ref(null)
    const filteredOptions = ref(null)

    const displayedOptions = computed(() => {
      return filteredOptions.value !== null ? filteredOptions.value : props.options
    })

    const handleFilter = (val, update) => {
      update(() => {
        if (val === '') {
          filteredOptions.value = null
        } else {
          const searchTerm = stringToSlug(val)
          filteredOptions.value = props.options.filter((option) => {
            const labelSlug = stringToSlug(option.label)
            const valueSlug = stringToSlug(option.value)
            return labelSlug.includes(searchTerm) || valueSlug.includes(searchTerm)
          })
        }
      })
    }

    const getSelectedLabel = (opt) => {
      if (typeof opt === 'string') {
        return opt
      }
      if (opt && opt.value) {
        return opt.value
      }
      return opt || ''
    }

    const isValidEmail = (email) => {
      if (!email || typeof email !== 'string') return false
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email.trim())
    }

    const validateEmailRule = (val) => {
      if (!val || val.length === 0) return true
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const allValid = val.every((email) => emailRegex.test(email))
      return allValid || props.errorMessage
    }

    const createValue = (val, done) => {
      const trimmedVal = val.trim()
      if (trimmedVal) {
        const emailExists = props.modelValue.includes(trimmedVal)
        if (!emailExists) {
          done(trimmedVal, 'add-unique')
        } else {
          done(false)
        }
      } else {
        done(false)
      }
    }

    return {
      selectRef,
      displayedOptions,
      handleFilter,
      getSelectedLabel,
      createValue,
      validateEmailRule,
      isValidEmail
    }
  }
}
</script>

<style lang="scss">
@use "src/css/mixins.scss" as *;
.select-content {
  max-height: max(250px, 25vh);
  overflow-y: scroll;
  @include scrollbars(5px, #6b6f7669, #6b6f76);
}
</style>
<style lang="scss" scoped>
@import "src/css/mixins.scss";
.filter-select {
  width: 100%;
  max-width: 100%;

  :deep(.q-field__control) {
    min-height: 37px;
    border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  :deep(.q-field__native) {
    font-size: 16px;
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  :deep(.q-field__append),
  :deep(.q-field__marginal),
  :deep(.q-chip__icon--remove) {
    flex: 0 0 auto;
  }

  :deep(.q-select__dropdown-icon) {
    font-size: 20px;
  }

  &.has-value {
    :deep(.q-field__native) {
      padding: 12px 0 0 0;
    }

    :deep(.q-field--auto-height .q-field__control),
    :deep(.q-field--auto-height .q-field__native) {
      height: 28px !important;
      min-height: 28px !important;
      max-height: 28px !important;
    }

    :deep(.q-field) {
      height: auto !important;
      max-height: 28px !important;
    }

    :deep(.q-field__native) {
      overflow-x: auto;
      overflow-y: hidden !important;
      width: 120px;
      box-sizing: border-box;
      position: relative;
      display: block;

      @include scrollbars(4px, #88888800, #55555500, #f1f1f100);

      &:hover {
        @include scrollbars(4px, #88888855, #55555599, #f1f1f1);
      }

      > * {
        max-width: 100%;
        min-width: 0;
      }
    }
  }

  :deep(.q-field--auto-height .q-field__control),
  :deep(.q-field--auto-height .q-field__native) {
    height: 53px !important;
    min-height: 53px !important;
  }

  :deep(.q-field__native) {
    white-space: nowrap;
    scrollbar-gutter: stable both-edges;
    overscroll-behavior-x: contain;
  }
}
</style>

